import React, { useState, useEffect } from 'react';

import { profileService } from '../services/profileService';
import { transactionService } from '../services/transactionService';
import TransactionMonitor from '../components/TransactionMonitor';
import QRCodeDisplay from '../components/QRCodeDisplay';
import type { Profile, CreateTransactionRequest, Transaction } from '../types/api';

const QRPaymentPage: React.FC = () => {

  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [selectedProfile, setSelectedProfile] = useState<string>('');
  const [amount, setAmount] = useState<string>('');
  const [remark, setRemark] = useState<string>('');
  const [clientId, setClientId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentTransaction, setCurrentTransaction] = useState<Transaction | null>(null);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<string>('');

  // Determine display currency based on selected profile
  const selectedProfileObj = profiles.find(p => p._id === selectedProfile);
  const selectedCurrency = selectedProfileObj
    ? (((selectedProfileObj.accbank || '').toLowerCase().includes('kbankpromptpay'))
        ? 'THB'
        : (selectedProfileObj.currency ? selectedProfileObj.currency.toUpperCase() : 'MYR'))
    : 'MYR';

  useEffect(() => {
    loadProfiles();
  }, []);

  const loadProfiles = async () => {
    try {
      setError('');
      console.log('🔄 Loading profiles for QR Payment...');
      const response = await profileService.getUserProfiles();

      console.log('📋 Profiles response:', response);

      if (response.success && response.data) {
        // Handle both array response and paginated response
        const profilesData = Array.isArray(response.data) ? response.data : (response.data.data || []);
        console.log('📋 Raw profiles data:', profilesData);

        // Only show active profiles
        const activeProfiles = profilesData.filter(p => p.status);
        console.log('✅ Active profiles:', activeProfiles.length, activeProfiles);
        setProfiles(activeProfiles);
      } else {
        const errorMsg = response.message || 'Failed to load profiles';
        setError(errorMsg);
        console.error('❌ Failed to load profiles:', errorMsg);
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to load profiles';
      setError(errorMsg);
      console.error('❌ Error loading profiles:', error);
    }
  };

  const handleCreatePayment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedProfile || !amount) {
      setError('Please select a profile and enter an amount');
      return;
    }

    try {
      setIsLoading(true);
      setError('');
      setSuccess('');

      const transactionData: CreateTransactionRequest = {
        profileId: selectedProfile,
        transaction: 'receiving',
        amount: parseFloat(amount),
        clientId: clientId || undefined,
        remark: remark || undefined,
        timeoutSeconds: 60 // 60 seconds default
      };

      // Validate transaction data
      const validationErrors = transactionService.validateTransactionData(transactionData);
      if (validationErrors.length > 0) {
        setError(validationErrors.join(', '));
        return;
      }

      const response = await transactionService.createTransaction(transactionData);

      if (response.success && response.data) {
        setCurrentTransaction(response.data);
        setSuccess('QR payment created successfully!');

        // Reset form
        setAmount('');
        setRemark('');
        setClientId('');
      } else {
        setError(response.message || 'Failed to create QR payment');
      }
    } catch (error: any) {
      setError(error.message || 'Failed to create QR payment');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    // Only allow numbers and decimal point
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const generateClientId = () => {
    const generatedId = transactionService.generateClientId('qr');
    setClientId(generatedId);
  };

  const handleTransactionUpdate = (transaction: Transaction) => {
    setCurrentTransaction((prev) => {
      const preservedQr = transaction.qrContent ?? prev?.qrContent;
      const preservedTimeout = transaction.timeoutSeconds ?? prev?.timeoutSeconds;
      const preservedTimeoutAt = transaction.timeoutAt ?? prev?.timeoutAt;
      return {
        ...(prev || {}),
        ...transaction,
        qrContent: preservedQr,
        timeoutSeconds: preservedTimeout,
        timeoutAt: preservedTimeoutAt
      } as Transaction;
    });

    // Clear success message after transaction is updated
    if (success) {
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  const handleCreateNewPayment = () => {


    setCurrentTransaction(null);
    setSuccess('');
    setError('');
  };

  // Ensure QR content is fetched when status is pendingpayment
  useEffect(() => {
    const fetchQrIfNeeded = async () => {
      if (!currentTransaction?._id) return;
      const status = (currentTransaction.statusTxt || '').toString().trim().toLowerCase();
      const hasQr = !!(currentTransaction.qrContent || currentTransaction.data?.qrContent);
      console.log(currentTransaction)
      if (status === 'pendingpayment' && !hasQr) {
        try {
          const res = await transactionService.getTransactionQR(currentTransaction._id);
          if (res.success && res.data?.qrContent) {
            setCurrentTransaction((prev) => (prev ? { ...prev, qrContent: prev.qrContent || res.data!.qrContent } as Transaction : prev));
          }
        } catch (err) {
          console.error('Failed to fetch QR content:', err);
        }
      }
    };
    fetchQrIfNeeded();
  }, [currentTransaction?._id, currentTransaction?.statusTxt]);


  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="mb-6">
            <h1 className="text-3xl font-bold text-gray-900">QR Payment</h1>
            <p className="text-gray-600 mt-2">
              Create QR codes for receiving payments
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Payment Form */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4">Create Payment</h2>

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">


                  {error}
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded mb-4">
                  {success}
                </div>
              )}

              <form onSubmit={handleCreatePayment} className="space-y-4">
                <div>
                  <div className="flex justify-between items-center mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      Payment Profile *
                    </label>
                    <button
                      type="button"
                      onClick={loadProfiles}
                      className="text-sm text-blue-600 hover:text-blue-800"
                    >
                      🔄 Refresh
                    </button>
                  </div>
                  <select
                    value={selectedProfile}
                    onChange={(e) => setSelectedProfile(e.target.value)}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  >
                    <option value="">Select a profile</option>
                    {profiles.map((profile) => (
                      <option key={profile._id} value={profile._id}>
                        {profile.accname} - {profileService.getBankDisplayName(profile.accbank)} ({profile.accno}) - Balance: {profileService.formatCurrency(profile.balance, profile.currency)}
                      </option>
                    ))}
                  </select>
                  {profiles.length === 0 && (
                    <p className="text-sm text-gray-500 mt-1">
                      No profiles available. <a href="/profiles" className="text-blue-600 hover:text-blue-800">Create a profile first</a>.
                    </p>
                  )}
                  {selectedProfile && (
                    <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md">
                      {(() => {
                        const profile = profiles.find(p => p._id === selectedProfile);
                        return profile ? (
                          <div className="text-sm text-blue-800">
                            <div><strong>Selected:</strong> {profile.accname}</div>
                            <div><strong>Bank:</strong> {profileService.getBankDisplayName(profile.accbank)}</div>
                            <div><strong>Account:</strong> {profile.accno}</div>
                            <div><strong>Balance:</strong> {profileService.formatCurrency(profile.balance, profile.currency)}</div>
                          </div>
                        ) : null;
                      })()}
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount ({selectedCurrency}) *
                  </label>
                  <input
                    type="text"
                    value={amount}
                    onChange={handleAmountChange}
                    placeholder="0.00"
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Client ID
                  </label>
                  <div className="flex space-x-2">
                    <input
                      type="text"
                      value={clientId}
                      onChange={(e) => setClientId(e.target.value)}
                      placeholder="Optional client identifier"
                      className="flex-1 border border-gray-300 rounded-md px-3 py-2"
                    />
                    <button
                      type="button"
                      onClick={generateClientId}
                      className="px-3 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
                    >
                      Generate
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remark
                  </label>
                  <textarea
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    placeholder="Optional payment description"
                    rows={3}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading || profiles.length === 0}
                  className="w-full bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white py-2 px-4 rounded-md font-medium"
                >
                  {isLoading ? 'Creating...' : 'Create QR Payment'}
                </button>
              </form>
            </div>

            {/* Transaction Monitor and QR Code Display */}
            <div className="bg-white shadow rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-semibold text-gray-900">Payment Status</h2>
                {currentTransaction && (
                  <button
                    onClick={handleCreateNewPayment}
                    className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                  >
                    ➕ New Payment
                  </button>
                )}
              </div>

              {currentTransaction ? (
                <div className="space-y-6">
                  {/* QR Code Display */}
                  {((currentTransaction.statusTxt || '').toLowerCase() === 'pendingpayment') && (currentTransaction.qrContent || currentTransaction.data?.qrContent) && (
                    <div className="flex justify-center">
                      <QRCodeDisplay
                        qrContent={currentTransaction.qrContent || currentTransaction.data?.qrContent}
                        timeoutSeconds={currentTransaction.timeoutSeconds}
                        timeoutAt={currentTransaction.timeoutAt}
                        title={`Payment QR Code`}
                        amount={currentTransaction.amount}
                        currency={selectedCurrency}
                        transactionStatus={currentTransaction.statusTxt}
                        errorMessage={currentTransaction.errorMessage}
                        onTimeout={() => {
                          console.log('QR Code expired for transaction:', currentTransaction._id);
                        }}
                      />
                    </div>
                  )}

                  {/* Transaction Monitor */}
                  <TransactionMonitor
                    transactionId={currentTransaction._id}
                    onTransactionUpdate={handleTransactionUpdate}
                    autoRefresh={true}
                    currency={selectedCurrency}
                  />
                </div>
              ) : (
                <div className="text-center py-12">
                  <div className="text-gray-400">
                    <svg className="mx-auto h-24 w-24 mb-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M3 3h8v8H3V3zm2 2v4h4V5H5zm8-2h8v8h-8V3zm2 2v4h4V5h-4zM3 13h8v8H3v-8zm2 2v4h4v-4H5zm10 0h2v2h-2v-2zm0 4h2v2h-2v-2zm4-4h2v2h-2v-2zm0 4h2v2h-2v-2zm-6-6h2v2h-2v-2zm2-2h2v2h-2v-2z"/>
                    </svg>
                    <p className="text-lg">No payment created</p>
                    <p className="text-sm mt-2">Create a payment to see QR code and status</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRPaymentPage;
